import {
  formatCourtDate,
  formatActDate,
  formatCourtTime,
  getActTime,
} from "@/Utils/litigationDates.mjs";

import BirthdayIcon from "../../../assets/images/birthdayicon.svg";
import DOIIcon from "../../../assets/images/incident.svg";
import "../LitigationCentral.css";
import SOLicon from "../../../assets/litigation/sol.svg";

import {
  LitigationCentralCalculationCell,
  LitigationCentralDependantDateCell,
  useLitigationCentralDateEntry,
} from "./LitigationCentralDateEntry";
import useViewportTableRows from "./useViewportTableRows";

const TrialsTab = ({ data = [], eventTypesData = [], fetchLitigationActs, onActClick }) => {
  const { fakeRowCount, tableBodyRef, tableContainerRef } = useViewportTableRows(data.length);
  const dateEntry = useLitigationCentralDateEntry({
    data,
    fetchLitigationActs,
  });

  const getServiceEntityDisplay = (contentType, entity) => {
    if (!entity) return null;

    const type = contentType?.toLowerCase();
    const iconClasses = {
      client: "ic-client",
      defendant: "ic-defendants",
      witness: "ic-witnesses",
      otherparty: "ic-parties",
    };

    let name = "";

    if (type === "defendant") {
      const defendantTypeId =
        entity?.defendantType?.id ??
        entity?.defendantType ??
        entity?.defendant_type?.id ??
        entity?.defendant_type;
      name =
        Number(defendantTypeId) === 1
          ? `${entity?.first_name || ""} ${entity?.last_name || ""}`.trim()
          : entity?.entity_name || "";
    } else if (type === "witness") {
      name = `${entity?.witness_first_name || entity?.first_name || ""} ${
        entity?.witness_last_name || entity?.last_name || ""
      }`.trim();
    } else if (type === "otherparty") {
      name =
        `${entity?.party_first_name || entity?.first_name || ""} ${
          entity?.party_last_name || entity?.last_name || ""
        }`.trim() ||
        entity?.entity_name ||
        "";
    } else if (type === "client") {
      name = `${entity?.first_name || ""} ${entity?.last_name || ""}`.trim();
    }

    return {
      iconClass: iconClasses[type] || "",
      name,
    };
  };

  const renderServiceInformation = (act) => {
    if (!act?.service_method || !act?.for_service) return null;

    const service = act?.service;
    const servedEntity = getServiceEntityDisplay(
      service?.served_content_type,
      service?.served_object
    );
    const serverEntity = getServiceEntityDisplay(
      service?.server_content_type,
      service?.server_object
    );
    const hasServiceEntities = servedEntity && serverEntity;
    const serviceText = [
      "Served",
      hasServiceEntities ? `To ${servedEntity.name} From ${serverEntity.name}` : "",
      act?.service_date ? `on ${formatActDate(act, "service_date")}` : "",
      "via",
      act.for_service.name,
      "Service",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className="d-flex align-items-center litigation-central-service-information"
        title={serviceText}
      >
        <span className="litigation-central-service-text">
          <span>
            Served
            {hasServiceEntities && (
              <>
                {" To "}
                {servedEntity.iconClass && (
                  <span>
                    <i
                      className={`ic ic-15 ${servedEntity.iconClass}`}
                      style={{ verticalAlign: "middle" }}
                    />{" "}
                    {servedEntity.name}
                  </span>
                )}
                {serverEntity.iconClass && (
                  <span>
                    {" From "}
                    <i
                      className={`ic ic-15 ${serverEntity.iconClass}`}
                      style={{ verticalAlign: "middle" }}
                    />{" "}
                    {serverEntity.name}
                  </span>
                )}
              </>
            )}
          </span>
          {act?.service_date && (
            <>
              {" on "}
              {formatActDate(act, "service_date")}
            </>
          )}
          {" via "}
          {act.for_service.name}
        </span>
      </div>
    );
  };

  const formatTimeWithoutLeadingZeros = formatCourtTime;

  return (
    <div>
      {/* Table */}
      <div ref={tableContainerRef} className="table-responsive">
        <table className="table table-borderless table-treatment has-height-25 litigation-central-table">
          <thead>
            <tr id="tb-header">
              <th style={{ width: "1%", whiteSpace: "nowrap" }}>#</th>
              <th style={{ width: "20px" }} />
              <th className="td-autosize text-center text-uppercase">Client</th>
              <th className="litigation-central-spacer" />

              <th className="td-autosize text-center text-uppercase">Case</th>
              <th className="litigation-central-spacer" />
              <th className="td-autosize text-center text-uppercase">Litigation Act</th>
              <th className="litigation-central-spacer" />
              <th className="td-autosize text-center text-uppercase">Additional events</th>
              <th className="litigation-central-spacer" />
              <th className="td-autosize text-center text-uppercase">Dependant Date</th>
              <th style={{ width: "1%" }} />
            </tr>
          </thead>
          <tbody ref={tableBodyRef} className="font-weight-600">
            {data?.map((act, idx) => {
              // const dependantTriggerDateName = act?.is_dependant_date
              //   ? act?.start_date_label ||
              //     act?.dependent_date_type_id?.dependent_date_name ||
              //     act?.name ||
              //     ""
              //   : act?.end_date_label ||
              //     act?.dependent_date_type_id?.dependent_date_name ||
              //     act?.name ||
              //     "";

              const isSOL = !act?.event_type_id;
              const act_eventTypeName =
                (isSOL && "Statute of Limitations") ||
                act?.event_type_id?.litigation_event_type ||
                "";

              const renderEventDate = (date, key) => {
                if (!date) return null;

                return (
                  <span key={key} className="litigation-central-event-date">
                    <span>{formatCourtDate(date)}</span>
                    {!act?.is_allday && (
                      <span>
                        {formatTimeWithoutLeadingZeros(
                          getActTime(act, key.includes("end") ? "end_date" : "start_date")
                        )}
                      </span>
                    )}
                  </span>
                );
              };

              const renderEventDates = () => {
                if (isSOL) return renderEventDate(act?.end_date, "sol-end-date");

                if (act?.uses_start_date && act?.uses_end_date) {
                  return (
                    <>
                      {renderEventDate(act?.start_date, "start-date")}
                      {act?.end_date && (
                        <span className="litigation-central-event-date-range-end">
                          <span className="text-primary-25">to</span>
                          {renderEventDate(act.end_date, "end-date")}
                        </span>
                      )}
                    </>
                  );
                }

                if (act?.uses_start_date) return renderEventDate(act?.start_date, "start-date");
                if (act?.uses_end_date) return renderEventDate(act?.end_date, "end-date");

                return null;
              };

              return (
                <tr
                  key={act.id}
                  className="cursor-pointer"
                  onClick={(event) => onActClick?.(act, event)}
                >
                  <td className="text-black">{idx + 1}</td>
                  <td />

                  {/* ----------------Client/CaseInfo Cell */}
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="icon-container"
                        style={{
                          display: "flex",
                          height: "19px",
                          width: "19px",
                          marginRight: "4px",
                        }}
                      >
                        {act?.for_case?.for_client?.profile_pic_29p ? (
                          <img
                            className="client_profile_image_63"
                            src={act?.for_case?.for_client?.profile_pic_29p}
                            alt="John Doe"
                          />
                        ) : (
                          <i className="ic ic-client-avatar h-100 w-100" />
                        )}
                        {/* <div className="border-overlay" /> */}
                      </div>
                      <div>
                        {act?.for_case?.for_client?.first_name +
                          " " +
                          act?.for_case?.for_client?.last_name}
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <img
                        style={{ height: "15px", width: "15px", marginRight: "5px" }}
                        src={BirthdayIcon}
                        alt={"birthday"}
                      />
                      {act?.for_case?.for_client?.birthday}
                    </div>
                    <div className="d-flex align-items-center">
                      <img
                        style={{ height: "15px", width: "15px", marginRight: "5px" }}
                        src={act?.for_case?.case_type?.casetype_icon}
                        alt={"car accident"}
                      />
                      {act?.for_case?.case_type?.name}
                    </div>
                    <div className="d-flex align-items-center">
                      <img
                        style={{ height: "15px", width: "15px", marginRight: "5px" }}
                        src={DOIIcon}
                        alt={"incident"}
                      />
                      {formatCourtDate(act?.for_case?.incident_date)}
                    </div>
                  </td>
                  <td />
                  {/* ----------------Litigation Case Name Cell */}
                  <td className="text-center">
                    <div>{act?.litigation?.case_short_name}</div>
                    <div>{act?.litigation?.case_number}</div>
                  </td>
                  <td />

                  {/* ----------------Litigation Event Details Cell */}
                  <td
                    className="litigation-central-event-cell"
                    style={{
                      fontWeight: "600",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    <div className="litigation-central-event-details">
                      <div className="litigation-central-event-heading">
                        {/* <span
                          aria-label={statusLabel}
                          className="litigation-central-status-icon"
                          role="img"
                          style={{ backgroundColor: statusColor }}
                          title={statusLabel}
                        /> */}
                        <span className="litigation-central-event-icon">
                          {isSOL ? (
                            <img src={SOLicon} alt="" className="ic-15" />
                          ) : (
                            <i
                              className={`ic ic-15 ic-${
                                act_eventTypeName === "Statute of Limitations"
                                  ? "Deadline"
                                  : act_eventTypeName
                              }`}
                            />
                          )}
                        </span>
                        <span className="litigation-central-event-content">
                          <span className="litigation-central-event-dates">
                            {renderEventDates()}
                          </span>
                          <span className="litigation-central-event-type text-uppercase">
                            {act_eventTypeName}:
                          </span>
                        </span>
                      </div>

                      <div
                        className="litigation-central-wrapped-text litigation-central-event-text"
                        style={{
                          fontWeight: "400",
                        }}
                      >
                        {act?.name}
                      </div>
                      {act?.description && (
                        <div
                          className="litigation-central-wrapped-text litigation-central-event-description"
                          style={{ fontWeight: "400" }}
                        >
                          {act.description}
                        </div>
                      )}
                      {act?.notes && (
                        <div
                          className="litigation-central-wrapped-text litigation-central-event-notes"
                          style={{
                            fontWeight: "400",
                          }}
                        >
                          {act.notes}
                        </div>
                      )}
                      {act?.service_method && (
                        <div
                          className="litigation-central-event-service"
                          style={{
                            fontWeight: "400",
                          }}
                        >
                          {renderServiceInformation(act)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td />

                  {/* ----------------related Calculated dates and additional events cell */}
                  <LitigationCentralCalculationCell
                    act={act}
                    entry={dateEntry}
                    eventTypesData={eventTypesData}
                  />
                  <td />

                  {/* ----------------Dependant Date input cell */}
                  <LitigationCentralDependantDateCell act={act} entry={dateEntry} />
                </tr>
              );
            })}
            {Array.from({ length: fakeRowCount }, (_, index) => (
              <tr
                key={`fake-row-${index}`}
                className="fake-row-2 height-21"
                style={{ height: "21px" }}
              >
                <td className="height-21" colSpan="12">
                  &nbsp;
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrialsTab;
